import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import type {
  DoctorAccountControlDoctorInbox,
  DoctorAccountControlDoctorSummary,
  DoctorAccountControlRequest,
} from "@jema-manager/shared";
import { doctorAccountControlApi } from "@shared/api/doctor-account-control";

const DOCTOR_CONTROL_IDLE_POLL_MS = 60_000;
const DOCTOR_CONTROL_PENDING_POLL_MS = 30_000;
const DOCTOR_CONTROL_ACTIVE_POLL_MS = 60_000;

export const doctorAccountControlQueryKeys = {
  all: ["doctor-account-control"] as const,
  doctorSummary: (doctorId: string) =>
    [...doctorAccountControlQueryKeys.all, "doctor-summary", doctorId] as const,
  doctorInbox: () => [...doctorAccountControlQueryKeys.all, "doctor-inbox"] as const,
  approvalDetail: (token: string) =>
    [...doctorAccountControlQueryKeys.all, "approval-detail", token] as const,
};

const resolveSummaryPollInterval = (
  summary: DoctorAccountControlDoctorSummary | undefined,
): number | false => {
  const status = summary?.openRequest?.status;

  if (status === "requested" || status === "approved") {
    return DOCTOR_CONTROL_PENDING_POLL_MS;
  }

  if (status === "active") {
    return DOCTOR_CONTROL_ACTIVE_POLL_MS;
  }

  return DOCTOR_CONTROL_IDLE_POLL_MS;
};

const resolveInboxPollInterval = (
  inbox: DoctorAccountControlDoctorInbox | undefined,
): number | false => {
  if (inbox?.pendingRequest) {
    return DOCTOR_CONTROL_PENDING_POLL_MS;
  }

  if (inbox?.activeSession) {
    return DOCTOR_CONTROL_ACTIVE_POLL_MS;
  }

  return DOCTOR_CONTROL_IDLE_POLL_MS;
};

const syncDoctorControlCaches = (
  queryClient: QueryClient,
  request: DoctorAccountControlRequest,
) => {
  queryClient.setQueryData<DoctorAccountControlDoctorSummary | undefined>(
    doctorAccountControlQueryKeys.doctorSummary(request.doctorId),
    (current) => ({
      ...current,
      openRequest:
        request.status === "ended" || request.status === "revoked" || request.status === "denied"
          ? null
          : request,
      activeSession: request.status === "active" ? request : null,
    }),
  );

  queryClient.setQueryData<DoctorAccountControlDoctorInbox | undefined>(
    doctorAccountControlQueryKeys.doctorInbox(),
    (current) => ({
      ...current,
      pendingRequest: request.status === "requested" ? request : null,
      activeSession: request.status === "active" ? request : null,
    }),
  );
};

export const useDoctorAccountControlDoctorSummary = (
  doctorId: string | null | undefined,
  enabled = true,
) =>
  useQuery({
    queryKey: doctorId ? doctorAccountControlQueryKeys.doctorSummary(doctorId) : [],
    queryFn: () => {
      if (!doctorId) {
        throw new Error('Doctor id is required');
      }
      return doctorAccountControlApi.getDoctorSummary(doctorId);
    },
    enabled: Boolean(doctorId && enabled),
    staleTime: 2_000,
    refetchInterval: (query) =>
      resolveSummaryPollInterval(query.state.data as DoctorAccountControlDoctorSummary | undefined),
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: "always",
  });

export const useDoctorAccountControlDoctorInbox = (enabled = true) =>
  useQuery({
    queryKey: doctorAccountControlQueryKeys.doctorInbox(),
    queryFn: () => doctorAccountControlApi.getMyDoctorInbox(),
    enabled,
    staleTime: 2_000,
    refetchInterval: (query) =>
      resolveInboxPollInterval(query.state.data as DoctorAccountControlDoctorInbox | undefined),
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: "always",
  });

export const useDoctorAccountControlApprovalDetail = (
  token: string | null | undefined,
  enabled = true,
) =>
  useQuery({
    queryKey: token ? doctorAccountControlQueryKeys.approvalDetail(token) : [],
    queryFn: () => {
      if (!token) {
        throw new Error('Approval token is required');
      }
      return doctorAccountControlApi.getApprovalDetail(token);
    },
    enabled: Boolean(token && enabled),
    retry: false,
    staleTime: 5_000,
  });

export const useCreateDoctorAccountControlRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: doctorAccountControlApi.create,
    onSuccess: (request) => {
      syncDoctorControlCaches(queryClient, request);
      void queryClient.invalidateQueries({
        queryKey: doctorAccountControlQueryKeys.doctorSummary(request.doctorId),
      });
    },
  });
};

export const useApproveDoctorAccountControlRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: doctorAccountControlApi.approve,
    onSuccess: (request) => {
      syncDoctorControlCaches(queryClient, request);
      void queryClient.invalidateQueries({
        queryKey: doctorAccountControlQueryKeys.doctorSummary(request.doctorId),
      });
      void queryClient.invalidateQueries({
        queryKey: doctorAccountControlQueryKeys.doctorInbox(),
      });
    },
  });
};

export const useDenyDoctorAccountControlRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: doctorAccountControlApi.deny,
    onSuccess: (request) => {
      syncDoctorControlCaches(queryClient, request);
      void queryClient.invalidateQueries({
        queryKey: doctorAccountControlQueryKeys.doctorSummary(request.doctorId),
      });
      void queryClient.invalidateQueries({
        queryKey: doctorAccountControlQueryKeys.doctorInbox(),
      });
    },
  });
};

export const useStartDoctorAccountControl = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: doctorAccountControlApi.start,
    onSuccess: (request) => {
      syncDoctorControlCaches(queryClient, request);
      void queryClient.invalidateQueries({
        queryKey: doctorAccountControlQueryKeys.doctorSummary(request.doctorId),
      });
      void queryClient.invalidateQueries({
        queryKey: doctorAccountControlQueryKeys.doctorInbox(),
      });
    },
  });
};

export const useEndDoctorAccountControl = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: doctorAccountControlApi.end,
    onSuccess: (request) => {
      syncDoctorControlCaches(queryClient, request);
      void queryClient.invalidateQueries({
        queryKey: doctorAccountControlQueryKeys.doctorSummary(request.doctorId),
      });
      void queryClient.invalidateQueries({
        queryKey: doctorAccountControlQueryKeys.doctorInbox(),
      });
    },
  });
};

export const useRevokeDoctorAccountControl = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: doctorAccountControlApi.revoke,
    onSuccess: (request) => {
      syncDoctorControlCaches(queryClient, request);
      void queryClient.invalidateQueries({
        queryKey: doctorAccountControlQueryKeys.doctorSummary(request.doctorId),
      });
      void queryClient.invalidateQueries({
        queryKey: doctorAccountControlQueryKeys.doctorInbox(),
      });
    },
  });
};
